import { useMemo } from 'react';
import { useTradingTheme } from '@trading-ui/core';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
  Decimation,
  CategoryScale,
  ChartOptions,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-date-fns';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { ChartUtils } from '@/utils/chart.utils';

import { generateTooltipContent } from './utils/tooltip';
import { PALETTE } from './constants';

import type { MultiSeriesChartProps } from './types';

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler,
  Decimation,
  annotationPlugin
);

ChartJS.defaults.font.family = 'MoeveSans, sans-serif';

export default function MultiSeriesChart({
  series,
  averages,
  height = '100%',
  width,
  yLabel,
  showLegend = true,
}: MultiSeriesChartProps) {
  const { palette } = useTradingTheme();
  const { t, i18n } = useTranslation();

  // Get locale based on current language
  const dateLocale = i18n.language === 'es' ? es : enUS;

  // Keep null values as null so they don't render
  const processedSeries = useMemo(
    () =>
      series.map((s) => ({
        ...s,
        data: s.data.map((point) => {
          if (point.y === null || point.y === undefined) {
            return { ...point, y: null };
          }
          return point;
        }),
      })),
    [series]
  );

  const datasets = useMemo(
    () =>
      processedSeries.map((s, i) => {
        const color = s.color ?? PALETTE[i % PALETTE.length];
        return {
          label: s.label,
          data: s.data,
          borderColor: color,
          backgroundColor: ChartUtils.withAlpha(color, 0.35),
          fill: false,
          pointRadius: 3,
          pointBackgroundColor: color,
          pointBorderColor: color,
          borderWidth: 2,
          tension: 0.2,
          spanGaps: false,
          parsing: { xAxisKey: 'x', yAxisKey: 'y' } as const,
        };
      }),
    [processedSeries]
  );

  const annotations = useMemo(() => {
    const items: Record<string, any> = {};
    if (averages?.nomination) {
      items.avgNom = {
        type: 'line',
        yMin: averages.nomination!,
        yMax: averages.nomination!,
        borderDash: [6, 6],
        borderWidth: 2,
        borderColor: '#0ea5e9',
        label: {
          display: true,
          position: 'end',
          backgroundColor: 'transparent',
          color: '#0ea5e9',
        },
      };
    }
    if (averages?.enagas) {
      items.avgEnagas = {
        type: 'line',
        yMin: averages.enagas!,
        yMax: averages.enagas!,
        borderDash: [6, 6],
        borderWidth: 2,
        borderColor: '#a78bfa',
        label: {
          display: true,
          position: 'end',
          backgroundColor: 'transparent',
          color: '#a78bfa',
        },
      };
    }
    return items;
  }, [averages]);

  const tooltipTitles = useMemo(
    () => ({
      averageNomination: t('consumer.chartPanel.averages.nomination'),
      averageEnagas: t('consumer.chartPanel.averages.enagas'),
    }),
    [t]
  );

  const options = useMemo(
    () =>
      ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index' as const, intersect: false },
        animation: false,
        normalized: true,
        plugins: {
          legend: {
            display: showLegend,
            position: 'bottom',
            align: 'start',
            labels: {
              usePointStyle: true,
              pointStyle: 'rectRounded',
              boxWidth: 12,
              boxHeight: 10,
              padding: 14,
              color: palette.text.primary,
            },
          },
          tooltip: {
            enabled: false, // default tooltip disabled
            position: 'nearest',
            external: (context) => generateTooltipContent(context, tooltipTitles, averages, dateLocale.code),
          },

          decimation: { enabled: true, algorithm: 'lttb', samples: 500 },
          annotation: { annotations },
        },
        scales: {
          x: {
            type: 'time' as const,
            time: {
              unit: 'day' as const,
              displayFormats: {
                day: 'd MMM',
              },
            },
            ticks: {
              maxRotation: 0,
              autoSkip: true,
              color: palette.text.primary,
              font: {
                size: 11,
              },
              callback: (value) => {
                if (typeof value === 'number') {
                  return format(new Date(value), 'd MMM', { locale: dateLocale });
                }
                return value;
              },
            },
            grid: { display: false },
          },
          y: {
            beginAtZero: true,
            title: { display: true, text: yLabel, color: palette.text.primary },
            grid: { color: 'rgba(0,0,0,0.06)' },
            ticks: {
              color: palette.text.primary,
              font: { size: 11 },
              callback(value) {
                if (typeof value === 'number') {
                  return new Intl.NumberFormat('es-ES', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                    useGrouping: true,
                  }).format(value);
                }
                return value;
              },
            },
          },
        },
        elements: { point: { radius: 0 } },
      } satisfies ChartOptions<'line'>),
    [annotations, averages, showLegend, yLabel, palette, dateLocale, tooltipTitles]
  );

  const data = useMemo(() => ({ datasets }), [datasets]);

  return (
    <div style={{ height, width, position: 'relative' }}>
      <Line data={data} options={options} />
    </div>
  );
}
